<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useMeta } from "@/composables/use-meta";
import axios from 'axios';
import { BASE_URL, BASE_URL_3 } from '@/Config/config';
import { useStore } from 'vuex';
import Swal from 'sweetalert2';
import { Modal } from 'bootstrap';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

useMeta({ title: "Violation Actions" });

const store = useStore();

// Search and filter variables
const searchValue = ref('');
const startDate = ref('');
const endDate = ref('');
const violationSearch = ref('');
const driverSearch = ref('');
const conductorSearch = ref('');
const busSearch = ref('');
const scheduleSearch = ref('');
const selectedViolationId = ref('');

// Loading states
const loading = ref(false);
const isLoading = ref(false);

// Data variables
const violations = ref([]);
const violationActions = ref([]);
const drivers = ref([]);
const actionsTaken = ref([]);
const violationParameters = ref([]);
const buses = ref([]);
const parameters = ref([]);
const schedules = ref([]);

// Store parameter names for each violation
const violationParamMap = ref({});

const driverLabel = (option) => `${option.driver}`;

const violationAction = ref({
    violation_id: '',
    driver_id: '',
    conductor_id: '',
    registration: '',
    route: '',
    schedule: '',
    // taken_action_id: null,
    parameters: {}
});

// Initialize modal
let createViolationActionModal = null;
// const modal = new bootstrap.Modal(document.getElementById('addViolationActionModal'));
// modal.show();

const openViolationModal = () => {
    if (!createViolationActionModal) {
        const el = document.getElementById('addViolationActionModal');
        if (el) {
            createViolationActionModal = new Modal(el);
        }
    }
    createViolationActionModal?.show();
};

const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        timer: 3000
    });
};

// Pagination variables
const currentPage = ref(1);
const itemsPerPage = ref(20);
const totalPages = computed(() => Math.ceil(filteredActions.value.length / itemsPerPage.value));

const filteredViolations = computed(() => {
    if (!violationSearch.value) return violations.value;
    const term = violationSearch.value.toLowerCase();
    return violations.value.filter(violation =>
        violation.title.toLowerCase().includes(term))
});

const filteredDrivers = computed(() => {
    if (!driverSearch.value) return drivers.value;
    const term = driverSearch.value.toLowerCase();
    return drivers.value.filter(driver =>
        driver.name.toLowerCase().includes(term))
});

const filteredBuses = computed(() => {
    if (!busSearch.value) return buses.value;
    const term = busSearch.value.toLowerCase();
    return buses.value.filter(bus =>
        bus.registration.toLowerCase().includes(term))
});

// Get all unique parameter names for the selected violation
const currentViolationParams = computed(() => {
    if (!selectedViolationId.value) return [];
    return violationParamMap.value[selectedViolationId.value] || [];
});

const filteredParams = computed(() => {
    if (!selectedViolationId.value) return [];
    return violationParameters.value.filter(param =>
        param.violation_id === selectedViolationId.value && param.is_active
    );
});

const filteredActions = computed(() => {
    let filtered = violationActions.value;

    // Filter by violation
    if (selectedViolationId.value) {
        filtered = filtered.filter(action => action.violation_id == selectedViolationId.value);
    }

    // Filter by date range
    if (startDate.value && endDate.value) {
        filtered = filtered.filter(action => {
            const actionDate = action.created_at ? new Date(action.created_at) : null;
            if (!actionDate) return false;
            return (
                actionDate >= new Date(startDate.value) &&
                actionDate <= new Date(endDate.value + 'T23:59:59')
            );
        });
    }

    // Filter by search term
    if (searchValue.value) {
        const keyword = searchValue.value.toLowerCase();
        filtered = filtered.filter(action => {
            const driver = drivers.value.find(d => d.id == action.driver_id);
            const actionTaken = actionsTaken.value.find(a => a.id == action.taken_action_id);

            return (
                (driver?.name || '').toLowerCase().includes(keyword) ||
                (action.bus_registration || '').toLowerCase().includes(keyword) ||
                (action.route || '').toLowerCase().includes(keyword) ||
                (action.violation?.title || '').toLowerCase().includes(keyword) ||
                (actionTaken?.name || '').toLowerCase().includes(keyword)
            );
        });
    }

    return filtered;
});

// Paginated data
const paginatedActions = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredActions.value.slice(start, end);
});

const violationCounts = computed(() => {
    const counts = {};

    // Initialize counts for all violations
    violations.value.forEach(violation => {
        counts[violation.id] = {
            title: violation.title,
            count: 0
        };
    });

    // Add 'All' option
    counts.all = {
        title: 'All Violations',
        count: violationActions.value.length
    };

    // Calculate counts for each violation
    violationActions.value.forEach(action => {
        if (counts[action.violation_id]) {
            counts[action.violation_id].count++;
        }
    });

    return counts;
});

const currentViolationCount = computed(() => {
    if (!selectedViolationId.value) {
        return violationCounts.value.all.count;
    }
    return violationCounts.value[selectedViolationId.value]?.count || 0;
});

// Pagination methods
const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const prevPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

const firstPage = () => {
    currentPage.value = 1;
};

const lastPage = () => {
    currentPage.value = totalPages.value;
};

// Reset to first page when filters change
watch([searchValue, startDate, endDate, selectedViolationId], () => {
    currentPage.value = 1;
});

const formatDate = (dateString) => {
    if (!dateString) return '—';
    const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    return new Date(dateString).toLocaleString(undefined, options);
};

const loadViolations = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/violations`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        violations.value = response.data.data || response.data;
        console.log("Violations loaded:", violations.value);
    } catch (error) {
        console.error('Error fetching violations:', error);
        showErrorAlert(error.message || 'Failed to load violations');
    }
};

// Load Violation Actions
const loadViolationActions = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/abs-violation-actions/index`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        const actions = response.data.data || response.data;

        violationActions.value = actions.map(action => {
            // Get numeric driver ID
            const driverId = typeof action.driver_id === 'string'
                ? parseInt(action.driver_id)
                : action.driver_id;

            // Find matching driver
            const driver = drivers.value.find(d => d.id === driverId);

            return {
                ...action,
                // Store both the numeric ID and the driver's actual name
                driver_id: driverId,
                driver_id: driver?.name || action.driver_id
            };
        });

        console.log("Mapped violation actions:", violationActions.value);
    } catch (error) {
        console.error('Error loading violation actions:', error);
        showErrorAlert(error.message || 'Failed to load violation actions');
    }
};

const loadDrivers = async () => {
    try {
        const response = await axios.get(`${BASE_URL_3}/company/employees/list?company_id=2`);
        drivers.value = response.data.employees || response.data;
        console.log("Drivers loaded:", drivers.value);
    } catch (error) {
        console.error('Error fetching employees:', error);
        showErrorAlert(error.message || 'Failed to load drivers');
    }
};

const loadActionsTaken = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/taken-actions`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        actionsTaken.value = response.data.data || response.data;
        console.log("Actions Taken loaded:", actionsTaken.value);
    } catch (error) {
        console.error('Error fetching actions taken:', error);
        showErrorAlert(error.message || 'Failed to load actions taken');
    }
};

const loadViolationParameters = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/violation-parameters`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        violationParameters.value = response.data.data || response.data;

        // Build parameter map for each violation
        violationParameters.value.forEach(param => {
            if (!violationParamMap.value[param.violation_id]) {
                violationParamMap.value[param.violation_id] = [];
            }
            violationParamMap.value[param.violation_id].push(param.name);
        });

        console.log("Violation Parameters loaded:", violationParameters.value);
    } catch (error) {
        console.error('Error fetching violation parameters:', error);
        showErrorAlert(error.message || 'Failed to load violation parameters');
    }
};

// Map IDs to names
const getDriverName = (driverId) => {
    // Convert driverId to number if it's stored as string in violation actions
    const idToFind = typeof driverId === 'string' ? parseInt(driverId) : driverId;

    // Find the driver in the drivers list
    const driver = drivers.value.find(d => d.id === idToFind);

    // Return the driver's name if found, otherwise return the original ID
    return driver?.name || driverId || '—';
};

const getActionTakenName = (actionTakenId) => {
    const action = actionsTaken.value.find(a => a.id == actionTakenId);
    return action?.name || actionTakenId || '—';
};

const loadBuses = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/navirec/vehicles`);
        const rawBuses = response.data.data || response.data;

        // Transform the data to only include id and registration
        buses.value = rawBuses.map(bus => ({
            id: bus.id,
            registration: bus.registration
        }));
        console.log("Buses loaded:", buses.value);
    } catch (error) {
        console.error('Error fetching Buses:', error);
        showErrorAlert('Failed to load Buses. Please try again later.');
    }
};

watch(() => violationAction.value.violation_id, async (newVal) => {
    const violationId = newVal?.id || newVal;
    if (violationId) {
        try {
            const response = await axios.get(`${BASE_URL}/violations/${violationId}`, {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            });
            const violationData = response.data;

            parameters.value = violationData.parameters || [];
            violationAction.value.parameters = {};

            parameters.value.forEach(param => {
                violationAction.value.parameters[param.name] = '';
            });

            console.log('Parameters loaded:', parameters.value);
        } catch (error) {
            console.error('Error loading violation parameters:', error);
            parameters.value = [];
            violationAction.value.parameters = {};
            showErrorAlert('Failed to load parameters for this violation');
        }
    } else {
        parameters.value = [];
        violationAction.value.parameters = {};
    }
}, { immediate: true });

const submitReport = async () => {
    try {
        loading.value = true;

        // Prepare the payload with string IDs
        const payload = {
            ...violationAction.value,
            driver_id: typeof violationAction.value.driver_id === 'object'
                ? violationAction.value.driver_id.id.toString()
                : violationAction.value.driver_id.toString() || '',
            driver: typeof violationAction.value.driver_id === 'object'
                ? violationAction.value.driver_id.name
                : '',
            conductor_id: violationAction.value.conductor_id,
            violation_id: typeof violationAction.value.violation_id === 'object'
                ? violationAction.value.violation_id.id
                : violationAction.value.violation_id,
            route: violationAction.value.route, // This now contains "DAR ES SALAAM - ARUSHA"
            // schedule: violationAction.value.schedule ?
            //   `${violationAction.value.schedule.departure_date} ${violationAction.value.schedule.departure_time}` :
            //   '',
            schedule: violationAction.value.schedule,
            bus_registration: typeof violationAction.value.registration === 'object'
                ? violationAction.value.registration.registration
                : violationAction.value.registration,
            // taken_action_id: violationAction.value.taken_action_id || null,
            parameters: violationAction.value.parameters
        };

        console.log("Payload to be sent:", payload);

        // Uncomment the following line to send the payload to the server

        const response = await axios.post(`${BASE_URL}/abs-violation-actions/store`,
            payload,
            {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            });

        if (response.data.success) {
            showSuccessAlert("Violation Action submitted successfully!");

            // Reset form
            violationAction.value = {
                violation_id: '',
                driver_id: '',
                conductor_id: '',
                route: '',
                schedule: null,
                // taken_action_id: '',
                bus_registration: '',
                parameters: {}
            };

            // Reset search fields
            driverSearch.value = '';
            busSearch.value = '';
            conductorSearch.value = '';
            scheduleSearch.value = '';
        } else {
            showErrorAlert(response.data.message || "Error submitting report");
        }
    } catch (error) {
        console.error("Submission error:", error);

        let errorMessage = "Failed to submit violation action";
        if (error.response) {
            if (error.response.data?.errors) {
                errorMessage = Object.entries(error.response.data.errors)
                    .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
                    .join('\n');
            } else if (error.response.data?.message) {
                errorMessage = error.response.data.message;
            }
        } else if (error.request) {
            errorMessage = "Network error - please check your connection";
        }

        showErrorAlert(errorMessage);
    } finally {
        loading.value = false;
    }
};

// Data loading
onMounted(async () => {
    isLoading.value = true;
    try {
        await loadDrivers();

        // Load all required data in parallel
        await Promise.all([
            loadViolations(),
            loadViolationActions(),
            loadActionsTaken(),
            loadBuses(),
            loadViolationParameters()
        ]);

    } catch (err) {
        console.error("Failed to load data:", err);
    } finally {
        isLoading.value = false;
    }
});

const clearFilters = () => {
    startDate.value = '';
    endDate.value = '';
    selectedViolationId.value = '';
    searchValue.value = '';
};
</script>

<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page">
                                    <span>Violation Actions</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <div class="row">
                            <div class="col-12 col-md-6 col-lg-9">
                                <h3 class="mb-0">Violation Action Reports.
                                    <strong v-if="selectedViolationId">
                                        Showing {{ currentViolationCount }} of {{ violationCounts.all.count }} total
                                        reports
                                        (Filtered by: {{violations.find(v => v.id == selectedViolationId)?.title}})
                                    </strong>
                                    <strong v-else>
                                        Showing all {{ violationCounts.all.count }} reports
                                    </strong>
                                </h3>
                                <!-- <router-link to="/abs/violation/action/add"
                                    class="btn btn-primary d-flex align-items-center">
                                    Add Violation Event
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="feather feather-plus ms-2">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                </router-link> -->
                            </div>
                            <div class="col-12 col-md-6 col-lg-3">
                                <button class="btn btn-primary d-flex align-items-center" @click="openViolationModal">
                                    Add Violation Event
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="feather feather-plus ms-2">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <!-- Filter controls -->
                        <div class="report-header mt-3">
                            <div class="filter-row row g-2 mb-2">
                                <div class="col-12 col-md-6 col-lg-3">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>From:</label>
                                        <input type="date" v-model="startDate" class="form-control">
                                    </div>
                                </div>
                                <div class="col-12 col-md-6 col-lg-3">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>To:</label>
                                        <input type="date" v-model="endDate" class="form-control">
                                    </div>
                                </div>
                                <div class="col-12 col-md-4">
                                    <input type="text" v-model="searchValue"
                                        placeholder="Search by driver, vehicle, route..." class="form-control">
                                </div>
                                <div class="col-12 col-md-6 col-lg-2">
                                    <button class="btn btn-danger w-100" @click="clearFilters">
                                        Clear Filters
                                    </button>
                                </div>
                            </div>

                            <!-- <div class="filter-row row g-2 mb-3">
                            </div> -->
                        </div>

                        <div class="violation-summary mb-3">
                            <div class="d-flex flex-wrap gap-2">
                                <button class="btn btn-sm"
                                    :class="!selectedViolationId ? 'btn-primary' : 'btn-outline-primary'"
                                    @click="selectedViolationId = ''">
                                    All ({{ violationCounts.all.count }})
                                </button>
                                <button v-for="violation in violations" :key="violation.id" class="btn btn-sm"
                                    :class="selectedViolationId == violation.id ? 'btn-info' : 'btn-outline-info'"
                                    @click="selectedViolationId = violation.id">
                                    {{ violation.title }} ({{ violationCounts[violation.id]?.count || 0 }})
                                </button>
                            </div>
                        </div>

                        <!-- Items per page selector -->
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <div class="d-flex align-items-center">
                                <span class="me-2">Show:</span>
                                <select v-model="itemsPerPage" class="form-select form-select-sm" style="width: 80px;">
                                    <option value="5">5</option>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                                <span class="ms-2">entries</span>
                            </div>
                            <div class="text-muted">
                                Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                                {{ Math.min(currentPage * itemsPerPage, filteredActions.length) }} of
                                {{ filteredActions.length }} entries
                            </div>
                        </div>

                        <!-- Loading state -->
                        <div v-if="isLoading" class="text-center p-4">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <p class="mt-2">Loading reports...</p>
                        </div>

                        <!-- Data table -->
                        <div v-else class="table-responsive">
                            <table class="table table-hover table-bordered">
                                <thead>
                                    <tr>
                                        <th>S/N</th>
                                        <th>Date</th>
                                        <th>Driver</th>
                                        <th>Conductor</th>
                                        <th>Vehicle</th>
                                        <th>Route</th>
                                        <th>Action Taken</th>
                                        <!-- Dynamic parameter headers -->
                                        <th v-for="param in currentViolationParams" :key="param">{{ param }}</th>
                                        <!-- Fallback for when no violation is selected -->
                                        <!-- <th v-if="!selectedViolationId">Parameters</th> -->
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(action, index) in paginatedActions" :key="action.id || index">
                                        <td>{{ (currentPage - 1) * itemsPerPage + index + 1 }}</td>
                                        <td>{{ formatDate(action.created_at) }}</td>
                                        <td>{{ getDriverName(action.driver_id) }}</td>
                                        <td>{{ action.conductor_id || '—' }}</td>
                                        <td>{{ action.bus_registration || '—' }}</td>
                                        <td>{{ action.route || '—' }}</td>
                                        <td>{{ getActionTakenName(action.taken_action_id) }}</td>

                                        <!-- Dynamic parameter cells -->
                                        <template v-if="selectedViolationId">
                                            <td v-for="param in currentViolationParams" :key="param">
                                                {{ action.parameters?.[param] || '—' }}
                                            </td>
                                        </template>
                                        <!-- <template v-else>
                                            <td>
                                                <div v-for="(value, key) in action.parameters" :key="key">
                                                    <strong>{{ key }}:</strong> {{ value || '—' }}
                                                </div>
                                            </td>
                                        </template> -->
                                    </tr>
                                    <tr v-if="filteredActions.length === 0">
                                        <td :colspan="8 + (selectedViolationId ? currentViolationParams.length : 1)"
                                            class="text-center text-muted">
                                            No reports found matching your criteria
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- Pagination -->
                            <div class="paginating-container pagination-solid flex-column align-items-center mt-4">
                                <ul role="menubar" aria-disabled="false" aria-label="Pagination"
                                    class="pagination mb-4 b-pagination">
                                    <li role="presentation" class="page-item first"
                                        :class="{ disabled: currentPage === 1 }">
                                        <button role="menuitem" type="button" @click="firstPage"
                                            :disabled="currentPage === 1" :tabindex="currentPage === 1 ? -1 : 0"
                                            aria-label="Go to first page" class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item prev"
                                        :class="{ disabled: currentPage === 1 }">
                                        <button role="menuitem" type="button" @click="prevPage"
                                            :disabled="currentPage === 1" :tabindex="currentPage === 1 ? -1 : 0"
                                            aria-label="Go to previous page" class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M15 19l-7-7 7-7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item" v-for="page in totalPages" :key="page"
                                        :class="{ active: currentPage === page }">
                                        <button role="menuitemradio" type="button" @click="goToPage(page)"
                                            :aria-label="`Go to page ${page}`" :aria-checked="currentPage === page"
                                            :aria-posinset="page" :aria-setsize="totalPages"
                                            :tabindex="currentPage === page ? 0 : -1" class="page-link">
                                            {{ page }}
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item next"
                                        :class="{ disabled: currentPage === totalPages }">
                                        <button role="menuitem" type="button" @click="nextPage"
                                            :disabled="currentPage === totalPages"
                                            :tabindex="currentPage === totalPages ? -1 : 0" aria-label="Go to next page"
                                            class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M9 5l7 7-7 7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item last"
                                        :class="{ disabled: currentPage === totalPages }">
                                        <button role="menuitem" type="button" @click="lastPage"
                                            :disabled="currentPage === totalPages"
                                            :tabindex="currentPage === totalPages ? -1 : 0" aria-label="Go to last page"
                                            class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M13 5l7 7-7 7M5 5l7 7-7 7">
                                                </path>
                                            </svg>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal -->
        <div class="modal fade" id="addViolationActionModal" tabindex="-1"
            aria-labelledby="addViolationActionModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-xl">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="addViolationActionModalLabel">Create Violation Action</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <div class="modal-body">
                        <!-- Main Form Starts -->
                        <form @submit.prevent="submitReport">
                            <!-- Violation Select -->
                            <div class="form-group">
                                <label for="violation">Select Violation</label>
                                <multiselect v-model="violationAction.violation_id" :options="filteredViolations"
                                    :searchable="true" :close-on-select="true" :clear-on-select="false"
                                    :preserve-search="true" placeholder="Select a Violation" label="title"
                                    track-by="id">
                                </multiselect>
                                <!-- <select v-model="violationAction.violation_id" class="form-control" id="violation">
                                    <option value="">Select a Violation</option>
                                    <option v-for="violation in filteredViolations" :key="violation.id"
                                        :value="violation.id">
                                        {{ violation.title }}
                                    </option>
                                </select> -->
                            </div>

                            <!-- Main Form Fields -->
                            <div class="row mt-3">
                                <!-- Left -->
                                <div class="col-md-6">
                                    <div class="form-group mt-3">
                                        <label for="employee">Select Driver</label>
                                        <multiselect v-model="violationAction.driver_id" :options="filteredDrivers"
                                            :searchable="true" :close-on-select="true" :clear-on-select="false"
                                            :preserve-search="true" placeholder="Select a Driver" label="name"
                                            track-by="id">
                                        </multiselect>
                                    </div>

                                    <div class="form-group mt-3">
                                        <label for="conductor">Select Conductor</label>
                                        <input type="text" class="form-control" v-model="violationAction.conductor_id"
                                            id="conductor" placeholder="Enter Conductor Name">
                                    </div>

                                    <div class="form-group mt-3">
                                        <label for="truck">Bus</label>
                                        <multiselect v-model="violationAction.registration" :options="filteredBuses"
                                            :searchable="true" :close-on-select="true" :clear-on-select="false"
                                            :preserve-search="true" placeholder="Select a Bus" label="registration"
                                            track-by="id">
                                        </multiselect>
                                    </div>
                                </div>

                                <!-- Right -->
                                <div class="col-md-6">
                                    <div class="form-group mt-3">
                                        <label for="schedule">Schedule</label>
                                        <input type="text" class="form-control" v-model="violationAction.schedule"
                                            id="schedule" placeholder="Departure Time and Date">
                                    </div>

                                    <div class="form-group mt-3">
                                        <label for="route">Route</label>
                                        <input type="text" class="form-control" v-model="violationAction.route"
                                            id="route" placeholder="Enter Route">
                                    </div>

                                    <div v-if="parameters.length" class="row">
                                        <div v-for="param in parameters" :key="param.id" class="col-md-6 mt-3">
                                            <label :for="'param-' + param.id">{{ param.name }}</label>

                                            <input v-if="param.datatype === 'text'" type="text" class="form-control"
                                                v-model="violationAction.parameters[param.name]"
                                                :id="'param-' + param.id">

                                            <input v-else-if="param.datatype === 'number'" type="number"
                                                class="form-control"
                                                v-model.number="violationAction.parameters[param.name]"
                                                :id="'param-' + param.id">

                                            <input v-else-if="param.datatype === 'datetime-local'" type="datetime-local"
                                                class="form-control" v-model="violationAction.parameters[param.name]"
                                                :id="'param-' + param.id">

                                            <input v-else-if="param.datatype === 'time'" type="time"
                                                class="form-control" v-model="violationAction.parameters[param.name]"
                                                :id="'param-' + param.id">

                                            <input v-else type="text" class="form-control"
                                                v-model="violationAction.parameters[param.name]"
                                                :id="'param-' + param.id">
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Submit -->
                            <div class="text-center mt-4">
                                <button v-if="loading" type="button" class="btn btn-success btn-lg me-3">
                                    <span class="spinner-grow spinner-grow-sm me-2" role="status"
                                        aria-hidden="true"></span>
                                    Processing...
                                </button>
                                <button v-else type="submit" class="btn btn-success btn-lg me-3">
                                    Submit
                                </button>
                            </div>
                        </form>
                        <!-- Main Form Ends -->
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- </div> -->
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}

.report-header {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 4px;
    margin-bottom: 1.5rem;
}

.filter-row {
    align-items: center;
}

.filter-row label {
    white-space: nowrap;
    margin-bottom: 0;
    font-weight: 500;
}

.text-success {
    color: #28a745;
}

.text-warning {
    color: #ffc107;
}

.text-danger {
    color: #dc3545;
}

.table-responsive {
    overflow-x: auto;
}

@media print {

    .report-header,
    .breadcrumb-one,
    .pagination {
        display: none !important;
    }
}

@media (max-width: 767.98px) {
    .filter-row>div {
        margin-bottom: 0.5rem;
    }
}

.violation-summary {
    padding: 0.75rem;
    border-radius: 4px;
}

.violation-summary .btn {
    min-width: 120px;
    text-align: center;
}

/* Pagination styles */
.pagination {
    display: flex;
    padding-left: 0;
    list-style: none;
    border-radius: 0.25rem;
}

.page-item {
    margin: 0 2px;
}

.page-item.disabled .page-link {
    color: #6c757d;
    pointer-events: none;
    background-color: #fff;
    border-color: #dee2e6;
}

.page-item.active .page-link {
    z-index: 3;
    color: #fff;
    background-color: #3b3f5c;
    border-color: #3b3f5c;
}

.page-link {
    position: relative;
    display: block;
    padding: 0.5rem 0.75rem;
    margin-left: -1px;
    line-height: 1.25;
    color: #3b3f5c;
    background-color: #fff;
    border: 1px solid #dee2e6;
    text-decoration: none;
}

.page-link:hover {
    z-index: 2;
    color: #1d1d1d;
    background-color: #e9ecef;
    border-color: #dee2e6;
}

.page-link:focus {
    z-index: 3;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(59, 63, 92, 0.25);
}
</style>